import { useState } from 'react';
import styled from 'styled-components';

import { colors } from 'styles/theme/colors';
import EyeOffOutlineIcon from 'svg/EyeOffOutlineIcon';
import EyeOutlineIcon from 'svg/EyeOutlineIcon';

import { formatSsn, maskSsn, SSN_LENGTH, ssnDigits } from './ssn';

// The SSN as shown on read-only surfaces: masked, with an eye to reveal it (UX, 2026-08-04).
// Both the header chips and the extraction card render it through here so the two can't drift —
// a masking rule that lives in two places eventually only holds in one.
//
// Revealing is NOT persisted — the state lives in this component, so leaving the referral unmounts
// it and the next visit is masked again. That's the whole security story: the default is masked on
// every arrival, and revealing is an explicit act each time rather than a preference that quietly
// sticks. Toggling back to masked (OLIO-2958 follow-up) is a separate, in-session act — it just
// stops the number from showing without unmounting/remounting anything.
export default function RevealableSsn({ ssn }: { ssn: string }) {
  const [revealed, setRevealed] = useState(false);

  if (revealed) {
    return (
      <>
        {/* data-dd-privacy: revealing shows the number to the person at the keyboard, not to
            Datadog — session replay records the DOM as rendered (defaultPrivacyLevel is 'allow'),
            so without this the reveal click would write the full SSN into the replay (OLIO-2958). */}
        {/* Only a complete number gets the dashed format — regrouping a redacted or partial
            capture (e.g. '7554', 'XXX-XX-7554') would present a plausible-looking fragment
            instead of the actual redacted/partial value it is (OLIO-3234 review). */}
        <span data-dd-privacy='mask'>{ssnDigits(ssn).length === SSN_LENGTH ? formatSsn(ssn) : ssn}</span>
        <Reveal
          type='button'
          onClick={() => setRevealed(false)}
          aria-label='Hide Social Security Number'
          data-testid='hide-ssn'>
          <EyeOffOutlineIcon width={14} height={14} />
        </Reveal>
      </>
    );
  }

  return (
    <>
      {maskSsn(ssn)}
      <Reveal
        type='button'
        onClick={() => setRevealed(true)}
        aria-label='Reveal Social Security Number'
        data-testid='reveal-ssn'>
        <EyeOutlineIcon width={14} height={14} />
      </Reveal>
    </>
  );
}

const Reveal = styled.button`
  background: none;
  border: none;
  padding: 0;
  margin-left: 6px;
  cursor: pointer;
  vertical-align: middle;
  color: ${colors.black50};
  line-height: 0;

  &:hover {
    color: ${colors.black100};
  }
`;
